import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Search } from "lucide-react";
import { api, errText } from "@/lib/api";
import { useCustomer } from "@/lib/customer";
import { fmtDate, fmtMoney } from "@/lib/format";
import { LdBadge, StageBadge, Mono } from "@/components/Badges";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const RECENT_KEY = "kanex.recentPos";

export function rememberPo(po) {
  if (!po?.lineId) return;
  const kept = readRecents().filter((r) => r.key !== po.key);
  const next = [{ ...po, seenAt: new Date().toISOString() }, ...kept].slice(0, 8);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
}

export function readRecents() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
  } catch {
    return [];
  }
}

function PoRow({ po, onOpen, testPrefix }) {
  return (
    <button
      onClick={() => onOpen(po)}
      className="flex w-full flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-background p-3 text-left transition-colors hover:border-primary hover:bg-accent"
      data-testid={`${testPrefix}-${po.lineId}`}
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{po.branchName || "Branch not named"}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          <Mono>{po.poNumber}</Mono> · {po.branchCode || "—"} · <Mono>{po.lineCount}</Mono> line
          {po.lineCount === 1 ? "" : "s"}
          {po.value ? <> · {fmtMoney(po.value)}</> : null}
          {po.poDate ? <> · <Mono>{fmtDate(po.poDate)}</Mono></> : null}
        </span>
      </span>
      <span className="flex shrink-0 flex-wrap items-center gap-2">
        {po.stageLabel && <StageBadge label={po.stageLabel} />}
        {!po.isClosed && po.ldColour && <LdBadge colour={po.ldColour} text={po.ldStatus} />}
      </span>
    </button>
  );
}

export default function PoHistory() {
  const { customerId } = useCustomer();
  const [term, setTerm] = useState("");
  const [pos, setPos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [recents, setRecents] = useState(readRecents());
  const navigate = useNavigate();

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/po-search", { params: { q } });
      setPos(data.pos);
      setTotal(data.total);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(term), 250);
    return () => clearTimeout(t);
  }, [term, load, customerId]);

  const open = (po) => {
    rememberPo(po);
    setRecents(readRecents());
    navigate(`/po-history/${po.lineId}`);
  };

  return (
    <div className="space-y-4" data-testid="po-history-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">PO history</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Find any PO — live or closed — and read its whole story. You can record updates from here too.
        </p>
      </div>

      <div className="panel p-3 sm:p-4">
        <Label className="field-label" htmlFor="po-history-search">
          Search by PO number, branch name or branch code
        </Label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="po-history-search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="209109, Muzaffarpur, 9008…"
            className="pl-9"
            data-testid="po-history-search-input"
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground" data-testid="po-history-count">
          {loading ? "Looking…" : `${total} PO${total === 1 ? "" : "s"} match. Showing ${pos.length}.`}
        </p>
        <div className="mt-3 space-y-2">
          {pos.map((po) => (
            <PoRow key={po.key} po={po} onOpen={open} testPrefix="po-history-result" />
          ))}
          {!loading && pos.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="po-history-empty">
              Nothing matches that. Try the branch code or part of the PO number.
            </p>
          )}
        </div>
      </div>

      {recents.length > 0 && (
        <div className="panel p-3 sm:p-4">
          <h2 className="flex items-center gap-2 text-sm font-medium">
            <Clock className="h-4 w-4" />
            Recently opened
          </h2>
          <div className="mt-3 space-y-2">
            {recents.map((po) => (
              <PoRow key={po.key} po={po} onOpen={open} testPrefix="po-history-recent" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
